import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, RadioButton } from '@zydalabs/storefront-components';
import { Button } from '@zydalabs/zac-react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { StyledStickySection, StyledTag } from 'common/styles';
import useTranslation from 'next-translate/useTranslation';
import { customHistory } from 'common/utils';
import { useFulfillmentSettings } from 'contexts/fulfillmentSettings/context';
import { isBranchBusyAtDateTime } from 'modules/checkoutModule/utils';
import { DateTime } from 'luxon';
import { isStoreClosedAtDate, getOpeningHoursByDay } from 'modules/locationsModule/utils';
import { useCartDetails } from 'service/hooks/cart';
import { StyledPageWrapper, StyledSchedulingTimesWrapper, StyledDeliveryTimesWrapper, StyledPrefix } from './styles';
import {
  usePersistedOrderMode,
  usePersistedFulfillmentTimeAndType,
  usePersistedBranchId,
  usePersistedCountryDetails,
  usePersistedSupportedDeliveryZone,
  usePersistedCartData,
} from '../../../../common/hooks/persistent';
import { FULFILLMENT_MODES, ORDER_TIME_TYPES } from '../../../../common/constants';
import EstimatedTimeLoading from './EstimatedTimeLoading';
import { useGlobalOrderTypeAndTime } from '../../../../common/hooks/global';
import { getEstimatedTimeText, getNextAvailableWorkingDayInterval, getSchedulingData } from './utils';
import { ScheduleDayType, SchedulingSlotType } from './types';

const StoreFrontHeaderComponent = dynamic(() => import('common/components/StoreFrontHeader'));

const EstimatedTimePage = () => {
  const router = useRouter();
  const { t } = useTranslation('estimatedTime');
  const lang = router.locale;
  const [mode] = usePersistedOrderMode();
  const [branchId] = usePersistedBranchId();
  const [globalOrderTypeAndTime, setGlobalOrderTypeAndTime] = useGlobalOrderTypeAndTime();
  const {
    isLoading,
    fulfillmentTimeType,
    isASAPAvailable,
    asapTimeText,
    isScheduledEnabled,
    schedulingTimes,
    scheduleDate,
    scheduleSlotTime,
  } = globalOrderTypeAndTime || {};
  const { branch } = useFulfillmentSettings();
  const { busyMode, busyFrom, busyUntil } = branch || {};
  const isBranchBusy = isBranchBusyAtDateTime(DateTime.now(), FULFILLMENT_MODES.DELIVERY, busyMode, busyFrom, busyUntil);
  const isDelivery = mode === FULFILLMENT_MODES.DELIVERY;

  const [persistedCountryDetails] = usePersistedCountryDetails();
  const [persistedSupportedDeliveryZone] = usePersistedSupportedDeliveryZone();
  const [, setPersistedFulfillmentTimeAndType] = usePersistedFulfillmentTimeAndType();
  const [cartData] = usePersistedCartData();
  const { data: cartDetails, isLoading: isCartDetailsLoading } = useCartDetails({
    ...cartData,
  });
  const { minPreprationTime, deliveryTime } = cartDetails || {};
  const [currentTimeType, setCurrentTimeType] = useState<string>(fulfillmentTimeType);

  const [currentDateAndSlot, setCurrentDateAndSlot] = useState<{
    scheduleDate: ScheduleDayType;
    scheduleSlotTime: SchedulingSlotType;
  }>({
    scheduleDate,
    scheduleSlotTime,
  });

  const openingTimeLabel = useMemo(() => {
    const { timeZone } = persistedCountryDetails || {};
    const openingHours = isDelivery ? persistedSupportedDeliveryZone?.openingHours : branch?.openingHours;
    if (!openingHours || !timeZone || isCartDetailsLoading) return null;
    const totalOrderProcessingTime = isDelivery ? minPreprationTime + deliveryTime : minPreprationTime;
    const isClosed =
      openingHours && timeZone
        ? isStoreClosedAtDate(openingHours, timeZone, DateTime.now().plus({ minutes: totalOrderProcessingTime || 0 }))
        : false;

    if (isClosed) {
      const nextOpeningDateTime = getNextAvailableWorkingDayInterval(
        getOpeningHoursByDay(openingHours),
        7, // Check next 7 days
      )?.start;

      if (nextOpeningDateTime) {
        const timeText = getEstimatedTimeText(mode, nextOpeningDateTime);
        return `${t('opensAt')} ${timeText[lang]}`;
      }
      return `${t('storeClosed')}`;
    }
    return null;
  }, [persistedSupportedDeliveryZone, persistedCountryDetails, mode, branch, isCartDetailsLoading]);

  useEffect(() => {
    const selectedTimeType = fulfillmentTimeType || (!fulfillmentTimeType && isScheduledEnabled && ORDER_TIME_TYPES.SCHEDULE);
    setCurrentTimeType(selectedTimeType);

    if (selectedTimeType === ORDER_TIME_TYPES.SCHEDULE) {
      if (schedulingTimes?.length > 0) {
        setCurrentDateAndSlot({
          scheduleDate: schedulingTimes[0],
          scheduleSlotTime: schedulingTimes[0]?.schedulingSlots?.[0],
        });
      } else {
        setCurrentDateAndSlot({ scheduleDate: null, scheduleSlotTime: null });
      }
    }
  }, [fulfillmentTimeType, isScheduledEnabled, lang]);

  if (isLoading)
    return <EstimatedTimeLoading headerTitle={t(mode === FULFILLMENT_MODES.DELIVERY ? 'deliveryTime' : 'pickupTime')} />;

  const ASAPTime = isASAPAvailable ? `${asapTimeText[lang]}` : t(ORDER_TIME_TYPES.ASAP);

  const scheduledOrders = [
    {
      content: ASAPTime,
      fieldValue: ORDER_TIME_TYPES.ASAP,
      disabled: !isASAPAvailable,
    },
    { content: t('scheduleOrder'), fieldValue: ORDER_TIME_TYPES.SCHEDULE },
  ];

  const AsapOrders = [
    {
      content: ASAPTime,
      fieldValue: ORDER_TIME_TYPES.ASAP,
      disabled: !isASAPAvailable,
    },
  ];

  const orderTimeOptions = isScheduledEnabled ? scheduledOrders : AsapOrders;

  const getAvailableSchedulingSlots = () => {
    if (!schedulingTimes) return [];

    return schedulingTimes.map(workingDay => ({
      name: workingDay.dayLongName[lang],
      tabValue: workingDay?.dateText[lang],
      component: (
        <StyledDeliveryTimesWrapper>
          <RadioButton
            key={lang}
            value={currentDateAndSlot?.scheduleSlotTime?.text[lang] || workingDay.schedulingSlots[0].text[lang]}
            name="slots"
            fields={workingDay?.schedulingSlots?.map(slot => ({
              content: slot.text[lang],
              fieldValue: slot.text[lang],
            }))}
            onChange={value => {
              const slot = workingDay.schedulingSlots.find(schedulingSlot => schedulingSlot.text[lang] === value);
              setCurrentDateAndSlot({ ...currentDateAndSlot, scheduleSlotTime: slot });
            }}
          />
        </StyledDeliveryTimesWrapper>
      ),
      prefix: <StyledPrefix>{workingDay?.dateText[lang]}</StyledPrefix>,
    }));
  };
  const actionButton = () => {
    if (currentTimeType === ORDER_TIME_TYPES.ASAP) {
      return {
        text: `${t('setTime')} ・ ${ASAPTime}`,
        onClick: () => {
          setGlobalOrderTypeAndTime({
            ...globalOrderTypeAndTime,
            fulfillmentTimeType: currentTimeType,
            estimatedTimeText: asapTimeText,
            actionButtonText: '',
          });
          setPersistedFulfillmentTimeAndType({
            fulfillmentType: currentTimeType,
            fulfillmentTime: null,
          });
          customHistory.handleOrderModeRouting({
            branchId,
          });
        },
      };
    }
    if (currentTimeType === ORDER_TIME_TYPES.SCHEDULE) {
      const setScheduledData = () => {
        const { scheduleDay, scheduleSlot, scheduleDayAndSlotText } = getSchedulingData(schedulingTimes, {
          scheduleDateText: currentDateAndSlot.scheduleDate.dateText,
          scheduleSlotTimeText: currentDateAndSlot.scheduleSlotTime.text,
        });
        setGlobalOrderTypeAndTime({
          ...globalOrderTypeAndTime,
          estimatedTimeText: scheduleDayAndSlotText,
          scheduleSlotInterval: scheduleSlot?.interval,
          scheduleDate: scheduleDay,
          scheduleSlotTime: scheduleSlot,
          fulfillmentTimeType: ORDER_TIME_TYPES.SCHEDULE,
          actionButtonText: '',
        });

        setPersistedFulfillmentTimeAndType({
          fulfillmentType: ORDER_TIME_TYPES.SCHEDULE,
          fulfillmentTime: {
            scheduleDateText: currentDateAndSlot.scheduleDate.dateText,
            scheduleSlotTimeText: currentDateAndSlot.scheduleSlotTime.text,
          },
        });
      };
      return {
        text: `${t('setTime')} ・ ${currentDateAndSlot?.scheduleDate?.dateText[lang]} ${
          currentDateAndSlot?.scheduleSlotTime?.text[lang]
        }`,
        onClick: () => {
          setScheduledData();
          customHistory.handleOrderModeRouting({
            branchId,
          });
        },
      };
    }
    return null;
  };
  return (
    <StyledPageWrapper>
      <StoreFrontHeaderComponent
        title={t(mode === FULFILLMENT_MODES.DELIVERY ? 'deliveryTime' : 'pickupTime')}
        onBack={() => customHistory.handleOrderModeRouting({ branchId })}
      />
      <StyledDeliveryTimesWrapper>
        {openingTimeLabel && <StyledTag>{openingTimeLabel}</StyledTag>}
        {!openingTimeLabel && !isASAPAvailable && isBranchBusy && <StyledTag>{t('branches:branchBusy.title')}</StyledTag>}
        <RadioButton
          value={currentTimeType}
          name="deliveryTime"
          fields={orderTimeOptions}
          onChange={value => {
            if (value === ORDER_TIME_TYPES.SCHEDULE)
              setCurrentDateAndSlot({
                scheduleDate: schedulingTimes[0],
                scheduleSlotTime: schedulingTimes[0].schedulingSlots[0],
              });
            setCurrentTimeType(value);
          }}
        />
      </StyledDeliveryTimesWrapper>
      {isScheduledEnabled && currentTimeType === ORDER_TIME_TYPES.SCHEDULE && (
        <StyledSchedulingTimesWrapper>
          <Tabs
            key={lang}
            tabs={getAvailableSchedulingSlots()}
            value={currentDateAndSlot?.scheduleDate?.dateText[lang] || ''}
            onChange={value => {
              const scheduleDay = schedulingTimes.find(schedulingTime => schedulingTime.dateText[lang] === value);
              setCurrentDateAndSlot({
                scheduleDate: scheduleDay,
                scheduleSlotTime: scheduleDay?.schedulingSlots?.[0],
              });
            }}
            variant="scheduled"
          />
        </StyledSchedulingTimesWrapper>
      )}
      {(isScheduledEnabled || isASAPAvailable) && (
        <StyledStickySection>
          <Button testId="scheduling_btn" text={actionButton()?.text} isFluid onClick={() => actionButton()?.onClick()} />
        </StyledStickySection>
      )}
    </StyledPageWrapper>
  );
};
export default EstimatedTimePage;
